// frontend/src/components/production-sequence/CalendarEditorDrawer.tsx
// Drawer ancho con el calendario laboral anual (12 meses). Clic en un día alterna
// laborable/no laborable. En memoria (mock); el botón Guardar queda para Dataverse.
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { buildCalendarMap } from '@/utils/sequenceCalculations';
import type { CalendarDay } from '@/types/productionSequence';
import { MonthMiniGrid } from './MonthMiniGrid';

interface Props {
  open: boolean;
  dark: boolean;
  calendarDays: CalendarDay[];
  onToggleDay: (date: string) => void;
  onReset: () => void;
  onClose: () => void;
}

function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function CalendarEditorDrawer({ open, dark, calendarDays, onToggleDay, onReset, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const [year, setYear] = useState(() => new Date().getFullYear());

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const calendarMap = useMemo(() => buildCalendarMap(calendarDays), [calendarDays]);
  const labelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of calendarDays) if (d.label) m.set(d.date, d.label);
    return m;
  }, [calendarDays]);
  const today = useMemo(() => localToday(), []);
  const weekdayLabels = useMemo(() => t('sequence.calendar.weekdays').split(','), [t]);
  const monthLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(i18n.language || 'es', { month: 'long' });
    return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(Date.UTC(year, m, 1))));
  }, [i18n.language, year]);

  if (!open) return null;

  const panel = dark ? 'bg-slate-900 border-white/10 text-slate-200' : 'bg-white border-slate-200 text-slate-700';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const iconBtn = `p-1.5 rounded-lg cursor-pointer ${dark ? 'hover:bg-white/10 text-slate-300' : 'hover:bg-slate-100 text-slate-600'}`;
  const btn = 'text-[12px] font-semibold px-3 py-1.5 rounded-lg cursor-pointer';

  const Legend = ({ cls, label }: { cls: string; label: string }) => (
    <span className="inline-flex items-center gap-1.5 text-[11px]">
      <span className={`w-3 h-3 rounded ${cls}`} />{label}
    </span>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} aria-hidden="true" />
      <aside className={`fixed inset-y-0 right-0 z-50 w-[960px] max-w-full border-l shadow-2xl flex flex-col ${panel}`}>
        {/* Cabecera */}
        <div className={`flex items-start justify-between px-5 py-3 border-b ${dark ? 'border-white/10' : 'border-slate-200'}`}>
          <div>
            <h2 className="text-[15px] font-semibold flex items-center gap-2"><span>📅</span>{t('sequence.calendar.title')}</h2>
            <p className={`text-[12px] ${muted}`}>{t('sequence.calendar.subtitle')}</p>
          </div>
          <button onClick={onClose} className={iconBtn} aria-label="close">
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l10 10M13 3L3 13" /></svg>
          </button>
        </div>

        {/* Navegación de año + leyenda */}
        <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-2.5 border-b ${dark ? 'border-white/10' : 'border-slate-200'}`}>
          <div className="flex items-center gap-2">
            <button onClick={() => setYear(y => y - 1)} className={iconBtn} aria-label={t('sequence.calendar.prevYear')}>
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 3L5 8l5 5" /></svg>
            </button>
            <span className="text-[18px] font-bold tabular-nums w-16 text-center">{year}</span>
            <button onClick={() => setYear(y => y + 1)} className={iconBtn} aria-label={t('sequence.calendar.nextYear')}>
              <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 3l5 5-5 5" /></svg>
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Legend cls={dark ? 'bg-slate-800/60 border border-white/10' : 'bg-white border border-slate-200'} label={t('sequence.calendar.legendWorkday')} />
            <Legend cls={dark ? 'bg-slate-800/60' : 'bg-slate-200'} label={t('sequence.calendar.legendWeekend')} />
            <Legend cls={dark ? 'bg-red-900/50' : 'bg-red-200'} label={t('sequence.calendar.legendHoliday')} />
            <Legend cls={dark ? 'bg-emerald-900/50' : 'bg-emerald-200'} label={t('sequence.calendar.legendWeekendWork')} />
            <Legend cls="ring-2 ring-sky-400" label={t('sequence.calendar.legendToday')} />
          </div>
        </div>

        {/* Rejilla de 12 meses */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className={`text-[11px] mb-3 ${muted}`}>ⓘ {t('sequence.calendar.hint')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {monthLabels.map((label, m) => (
              <MonthMiniGrid
                key={m}
                year={year}
                month0={m}
                monthLabel={label}
                weekdayLabels={weekdayLabels}
                calendarMap={calendarMap}
                labelMap={labelMap}
                today={today}
                dark={dark}
                onToggleDay={onToggleDay}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between gap-2 px-5 py-3 border-t ${dark ? 'border-white/10' : 'border-slate-200'}`}>
          <button onClick={onReset} className={`${btn} ${dark ? 'bg-white/8 text-slate-300 hover:bg-white/12' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            {t('sequence.calendar.reset')}
          </button>
          <button disabled title={t('sequence.calendar.saveTooltip')} className={`${btn} bg-slate-300/40 text-slate-500 cursor-not-allowed`}>
            💾 {t('sequence.calendar.save')}
          </button>
        </div>
      </aside>
    </>
  );
}
